import { IBitrixActiveCourtsResponse, IBitrixDailyScheduleResponse, IBitrixCourt, IBitrixScheduleItem } from "./bitrixArenasTypes";
import { IArena } from "./types";
import { IHall, ITime } from "../halls/types";

const formatTimeFromISO = (isoString: string): string => {
  const date = new Date(isoString);
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
};

export const mapBitrixDataToArenas = (
  activeCourts: IBitrixActiveCourtsResponse,
  dailySchedule: IBitrixDailyScheduleResponse
): IArena[] => {
  const courts = activeCourts.activeCourts || [];
  const schedule = dailySchedule.schedule || [];
  
  const scheduleMap = new Map<string, IBitrixScheduleItem>();
  schedule.forEach((item) => {
    scheduleMap.set(item.courtId, item);
  });
  
  return courts.map((court: IBitrixCourt, index: number) => {
    const scheduleItem = scheduleMap.get(court.courtId);
    const rentInfo = scheduleItem?.rentInfo || [];
    
    const seats = rentInfo.map((rent: any, rentIndex: number) => {
      const startTime = formatTimeFromISO(rent.startTime);
      return {
        id: rentIndex + 1,
        court_id: index + 1,
        time: startTime,
        price: rent.price,
        price_weekend: rent.price,
        is_special_price: false,
      };
    });
    
    const unAvailableSeats: { [key: number]: number[] } = {};
    rentInfo.forEach((rent: any, rentIndex: number) => {
      if (rent.busy) {
        const courtId = index + 1;
        if (!unAvailableSeats[courtId]) {
          unAvailableSeats[courtId] = [];
        }
        unAvailableSeats[courtId].push(rentIndex + 1);
      }
    });
    
    return {
      id: index + 1,
      title: court.courtName,
      courts: [
        {
          id: index + 1,
          active: 1,
          arena_id: index + 1,
          cell_color: '',
          created_at: new Date().toISOString(),
          sort: index + 1,
          title: court.courtName,
          updated_at: new Date().toISOString(),
        },
      ],
      seats: seats,
      cell_color: '',
      unAvailableSeats: unAvailableSeats,
    };
  });
};

export const mapBitrixDataToHalls = (
  activeCourts: IBitrixActiveCourtsResponse,
  dailySchedule: IBitrixDailyScheduleResponse
): IHall[] => {
  const courts = activeCourts.activeCourts || [];
  const schedule = dailySchedule.schedule || [];
  
  const scheduleMap = new Map<string, IBitrixScheduleItem>();
  schedule.forEach((item) => {
    scheduleMap.set(item.courtId, item);
  });
  
  return courts.map((court: IBitrixCourt, index: number) => {
    const scheduleItem = scheduleMap.get(court.courtId);
    const rentInfo = scheduleItem?.rentInfo || [];
    
    const prices = rentInfo.map((rent: any) => rent.price);
    const minPrice = prices.length > 0 ? Math.min(...prices) : 0;
    const maxPrice = prices.length > 0 ? Math.max(...prices) : 0;
    
    const priceByHour: { [key: number]: number } = {};
    rentInfo.forEach((rent: any) => {
      const hour = new Date(rent.startTime).getHours();
      if (hour >= 7 && hour <= 23) {
        priceByHour[hour] = rent.price;
      }
    });
    
    return {
      id: index + 1,
      courtId: court.courtId,
      active: 1,
      created_at: null,
      image_url: '',
      images: [],
      price: minPrice,
      prime_price: maxPrice,
      title: court.courtName,
      updated_at: new Date().toISOString(),
      price_7: priceByHour[7] || minPrice,
      price_8: priceByHour[8] || minPrice,
      price_9: priceByHour[9] || minPrice,
      price_10: priceByHour[10] || minPrice,
      price_11: priceByHour[11] || minPrice,
      price_12: priceByHour[12] || minPrice,
      price_13: priceByHour[13] || minPrice,
      price_14: priceByHour[14] || minPrice,
      price_15: priceByHour[15] || minPrice,
      price_16: priceByHour[16] || minPrice,
      price_17: priceByHour[17] || minPrice,
      price_18: priceByHour[18] || minPrice,
      price_19: priceByHour[19] || minPrice,
      price_20: priceByHour[20] || minPrice,
      price_21: priceByHour[21] || minPrice,
      price_22: priceByHour[22] || minPrice,
      price_23: priceByHour[23] || minPrice,
    };
  });
};

export const mapBitrixScheduleToTimes = (
  dailySchedule: IBitrixDailyScheduleResponse,
  hallId: number | string
): ITime[] => {
  const schedule = dailySchedule.schedule || [];
  
  const scheduleItem = schedule.find((item) => 
    item.courtId === String(hallId) || 
    item.courtId === hallId
  );
  
  if (!scheduleItem) {
    return [];
  }
  
  return scheduleItem.rentInfo.map((rent: any, index: number) => {
    const startTime = formatTimeFromISO(rent.startTime);
    const finishTime = formatTimeFromISO(rent.finishTime);
    
    const code = `${startTime}-${finishTime}`;
    
    return {
      id: index + 1,
      title: startTime,
      code: code,
      active: rent.busy ? 0 : 1,
      created_at: null,
      updated_at: null,
      price: rent.price,
      startTime: rent.startTime,
      finishTime: rent.finishTime,
      busy: rent.busy || false,
      busyWithYou: rent.busyWithYou || false,
    };
  });
};

